import { CASH_PRODUCT_TYPE } from '@repo/constants';
import { FormValidationMessage } from '@repo/constants';
import { z } from 'zod';

export const MultipleTradeSchema = z
  .object({
    tradeDate: z.string({
      required_error: FormValidationMessage.Required,
      invalid_type_error: FormValidationMessage.Required,
    }),
    settlementDate: z.string({
      required_error: FormValidationMessage.Required,
      invalid_type_error: FormValidationMessage.Required,
    }),
    securityType: z.number({
      required_error: FormValidationMessage.Required,
      invalid_type_error: FormValidationMessage.Required,
    }),
    securitySubType: z.number().optional(),
    securityId: z.number().optional(),
    cashCurrency: z.number().optional().nullable(),
    market: z.number({
      required_error: FormValidationMessage.Required,
      invalid_type_error: FormValidationMessage.Required,
    }),
    side: z.number({
      required_error: FormValidationMessage.Required,
      invalid_type_error: FormValidationMessage.Required,
    }),
    broker: z.number({
      required_error: FormValidationMessage.Required,
      invalid_type_error: FormValidationMessage.Required,
    }),
    quantity: z.string({
      required_error: FormValidationMessage.Required,
      invalid_type_error: FormValidationMessage.Required,
    }),
    grossPrice: z.string({
      required_error: FormValidationMessage.Required,
      invalid_type_error: FormValidationMessage.Required,
    }),
    tradeCcy: z.number({
      required_error: FormValidationMessage.Required,
      invalid_type_error: FormValidationMessage.Required,
    }),
    settlementCcy: z.number({
      required_error: FormValidationMessage.Required,
      invalid_type_error: FormValidationMessage.Required,
    }),
    couponAccrual: z.string({
      required_error: FormValidationMessage.Required,
      invalid_type_error: FormValidationMessage.Required,
    }),
    conversionRate: z.string({
      required_error: FormValidationMessage.Required,
      invalid_type_error: FormValidationMessage.Required,
    }),

    clients: z.array(
      z.object({
        clientAccount: z
          .number({
            required_error: FormValidationMessage.Required,
            invalid_type_error: FormValidationMessage.Required,
          })
          .min(1, {
            message: FormValidationMessage.Required,
          }),
        quantity: z
          .string({
            required_error: FormValidationMessage.Required,
            invalid_type_error: FormValidationMessage.Required,
          })
          .min(1, {
            message: FormValidationMessage.Required,
          })
          .refine((val) => Number(val) > 0, {
            message: 'Quantity must be greater than 0',
          }),
        commissionType: z.number().optional().nullable(),
        commissionRate: z.string().optional().nullable(),
        commissionAmount: z.string().optional().nullable(),
        grossAmount: z.string({
          required_error: FormValidationMessage.Required,
          invalid_type_error: FormValidationMessage.Required,
        }),
        netAmount: z.string({
          required_error: FormValidationMessage.Required,
          invalid_type_error: FormValidationMessage.Required,
        }),
      }),
    ),
  })
  .superRefine((data, ctx) => {
    // Security Sub Type is required for cash product type
    if (data.securityType === CASH_PRODUCT_TYPE && !data.securitySubType) {
      ctx.addIssue({
        path: ['securitySubType'],
        code: z.ZodIssueCode.custom,
        message: FormValidationMessage.Required,
      });
    }
    // Cash Currency is required for cash product type
    if (data.securityType === CASH_PRODUCT_TYPE && !data.cashCurrency) {
      ctx.addIssue({
        path: ['cashCurrency'],
        code: z.ZodIssueCode.custom,
        message: FormValidationMessage.Required,
      });
    }
    // Security Id is required for non-cash product type
    if (data.securityType !== CASH_PRODUCT_TYPE && !data.securityId) {
      ctx.addIssue({
        path: ['securityId'],
        code: z.ZodIssueCode.custom,
        message: FormValidationMessage.Required,
      });
    }
    // Conversion Rate is required if trade ccy is not the same as settlement ccy
    if (data.tradeCcy !== data.settlementCcy && !data.conversionRate) {
      ctx.addIssue({
        path: ['conversionRate'],
        code: z.ZodIssueCode.custom,
        message: FormValidationMessage.Required,
      });
    }
    // Check if the sum of client quantities matches the quantity indicated in the form
    const sumQuantity = data.clients.reduce(
      (acc, client) => acc + Number(client.quantity),
      0,
    );
    if (sumQuantity !== Number(data.quantity)) {
      ctx.addIssue({
        path: ['clients', data.clients.length - 1, 'quantity'],
        code: z.ZodIssueCode.custom,
        message:
          'The total amount of quantity from clients must be equal to the quantity indicated in the form',
      });
    }

    // Ensure client accounts are unique across allocations
    const seenAccounts = new Map<number, number>();
    data.clients.forEach((client, index) => {
      const account = client.clientAccount;
      if (!account) return;
      if (seenAccounts.has(account)) {
        ctx.addIssue({
          path: ['clients', index, 'clientAccount'],
          code: z.ZodIssueCode.custom,
          message: 'Client account must be unique',
        });
      } else {
        seenAccounts.set(account, index);
      }
    });
  });

export type MultipleTradeSchema = z.infer<typeof MultipleTradeSchema>;
